// ============================================================================
// Incentive Program data + actions hook.
//
// Backs the Incentive module (month-end queue, payouts review, analytics,
// finance handoff). Reads the security_invoker views built by the
// incentive_program_* migrations (2026-06-05), so RLS scopes every result:
// finance + full-AM see all; an account_manager sees only their facilities.
//
// Pattern matches the rest of the app: plain useState/useEffect exposing a
// { data, isLoading, error, refetch } surface (no TanStack Query installed).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import type {
  IncentivePayoutStatus,
  IncentivePayoutType,
} from '../../../shared/types';
import { errorMessage } from '../../../shared/errors';
import { fetchAmNameMap } from '../../../shared/am-name';
import { navBadgeCount, type NavBadgeCounts } from '../nav-badge';

// ----- View row shapes (mirror the SQL views) ------------------------------

export type QueueStatus =
  | 'not_generated'
  | 'needs_validation'
  // Every row has been validated by its AM and is now waiting on the
  // approver (National AM). Added by the two-step migration, 2026-08-13.
  | 'needs_approval'
  | 'ready_for_finance'
  | 'complete';

export interface PeriodNeedingReview {
  period: string;
  payout_type: IncentivePayoutType;
  start_date: string;
  end_date: string;
  expected_count: number;
  generated_count: number;
  pending_review_count: number;
  am_validated_count: number;
  disqualified_count: number;
  verified_count: number;
  sent_to_finance_count: number;
  paid_count: number;
  queue_status: QueueStatus;
}

export interface PayoutDetail {
  payout_id: string;
  payout_type: IncentivePayoutType;
  period: string;
  status: IncentivePayoutStatus;
  amount: number;
  recipient_type: string;
  assignment_id: string | null;
  facility_id: string | null;
  facility_name: string | null;
  market_region: string | null;
  account_manager_eid: string | null;
  account_manager_name: string | null;
  account_id: string | null;
  account_name: string | null;
  provider_id: string | null;
  provider_name: string | null;
  provider_owner_name: string | null;
  worker_name: string | null;
  // Step 1 stamp — the owning AM (CW-N, 2026-08-13).
  am_validated_by_eid: string | null;
  am_validated_at: string | null;
  am_validated_by_name: string | null;
  // Step 2 stamp — the approver.
  verified_by_eid: string | null;
  verified_at: string | null;
  routed_to_finance_at: string | null;
  paid_at: string | null;
  // Engine-written reason line, e.g. "Partial month — sub started 2026-07-20"
  // or the AP-reconstruction note on April/May.
  notes: string | null;
  disqualifying_feedback_id: string | null;
  disqualifying_feedback_desc: string | null;
  disqualifying_feedback_date: string | null;
  disqualifying_feedback_sentiment: string | null;
  override_justification: string | null;
  overridden_by_eid: string | null;
  qbo_bill_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CostByPeriod {
  period: string;
  payout_type: IncentivePayoutType;
  total_rows: number;
  paying_rows: number;
  paid_rows: number;
  incentive_cost: number;
  paid_cost: number;
}

export interface PayoutRateBySub {
  provider_id: string | null;
  provider_name: string | null;
  period: string;
  nmi_rows: number;
  paid_rows: number;
  disqualified_rows: number;
  paid_amount: number;
  payout_rate_pct: number | null;
}

export interface ComplimentRate {
  period: string;
  eligible_assignments: number;
  earned_rows: number;
  paid_rows: number;
  compliment_cost: number;
}

export interface FinanceHandoffRow {
  payout_id: string;
  payout_type: IncentivePayoutType;
  period: string;
  status: IncentivePayoutStatus;
  assignment_id: string | null;
  facility_id: string | null;
  facility_name: string | null;
  market_region: string | null;
  account_id: string | null;
  account_name: string | null;
  provider_id: string | null;
  provider_name: string | null;
  provider_owner_name: string | null;
  worker_name: string | null;
  recipient_type: string;
  amount: number;
  verified_at: string | null;
  verified_by_eid: string | null;
  routed_to_finance_at: string | null;
  qbo_bill_id: string | null;
  override_justification: string | null;
}

// ----- Live tracker (the OPEN month + OPEN quarter) -------------------------
//
// v_incentive_live_tracker is assignment grain, not payout grain: the open
// period has no payout rows yet, so no other view can describe it. Nothing is
// ever paid from the open period — this is visibility only.

export type NoMissTrackerState = 'on_track' | 'not_qualified' | 'ineligible';
export type ComplimentTrackerState = 'qualified' | 'not_yet' | 'ineligible';
export type TrackerState = NoMissTrackerState | ComplimentTrackerState;

export interface LiveTrackerRow {
  payout_type: IncentivePayoutType;
  period: string;
  period_start: string;
  period_end: string;
  assignment_id: string | null;
  facility_id: string | null;
  facility_name: string | null;
  market_region: string | null;
  account_id: string | null;
  account_name: string | null;
  account_manager_eid: string | null;
  account_manager_name: string | null;
  provider_id: string | null;
  provider_name: string | null;
  provider_owner_name: string | null;
  worker_name: string | null;
  crew_size: number | null;
  service_start_date: string | null;
  potential_amount: number;
  tracker_state: TrackerState;
  /** Already written as display text by the view — render it verbatim. */
  reason: string | null;
  disqualifying_feedback_id: string | null;
  compliment_count: number;
}

// One query returns both trackers; callers split on payout_type.
//
// `refreshKey` is the cross-view freshness signal (CW-N 3d): the page owns a
// counter, every mutating screen bumps it, and every reader re-runs. It is a
// plain number rather than a state library because this module has none and
// is not the place to introduce one.
export function useIncentiveLiveTracker(refreshKey = 0) {
  const [rows, setRows] = useState<LiveTrackerRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data, error: e } = await supabase
          .from('v_incentive_live_tracker')
          .select('*')
          .order('facility_name', { ascending: true });
        if (cancelled) return;
        if (e) throw e;
        setRows((data ?? []) as LiveTrackerRow[]);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load the live tracker.', 'incentive'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick, refreshKey]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { rows, isLoading, error, refetch };
}

// ----- Month-end queue ------------------------------------------------------

export function useIncentiveQueue(refreshKey = 0) {
  const [rows, setRows] = useState<PeriodNeedingReview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data, error: e } = await supabase
          .from('v_incentive_periods_needing_review')
          .select('*');
        if (cancelled) return;
        if (e) throw e;
        const sorted = ((data ?? []) as PeriodNeedingReview[]).slice().sort(
          (a, b) =>
            a.payout_type.localeCompare(b.payout_type) ||
            b.period.localeCompare(a.period),
        );
        setRows(sorted);
      } catch (err) {
        if (cancelled) return;
        console.error('[incentive] queue load failed', err);
        setError(errorMessage(err, 'Failed to load queue.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick, refreshKey]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { rows, isLoading, error, refetch };
}

// Nav-badge count: payouts awaiting a HUMAN across visible periods — step 1
// (pending_review) plus step 2 (am_validated).
//
// CW-Q 4.1. This selected `pending_review_count` alone, which was correct until
// CW-N split validation into two steps. After that split the badge counted only
// step 1, so the moment the last AM validated the last row the nav badge went to
// zero and **Jack's approval queue disappeared from the nav** — with real money
// sitting in it, waiting on him specifically. The exact failure the two-step
// model created, and it is one line.
export function useIncentivePendingReviewCount(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('v_incentive_periods_needing_review')
          .select('pending_review_count, am_validated_count');
        if (cancelled || error || !data) return;
        setCount(navBadgeCount(data as NavBadgeCounts[]));
      } catch {
        /* badge is best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return count;
}

// ----- Approver name resolution --------------------------------------------
//
// v_incentive_payouts_detail returns am_validated_by_name but only
// verified_by_eid, so "Approved by Jack Stuber" needs one more lookup.
// fetchAmNameMap is the canonical resolver (src/shared/am-name.ts): it reads
// v_am_name, which is security_invoker=false and granted to authenticated —
// employees RLS would return NULL names for an account_manager.
export function useEmployeeNames(eids: ReadonlyArray<string | null>) {
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const key = Array.from(new Set(eids.filter(Boolean) as string[]))
    .sort()
    .join(',');

  useEffect(() => {
    let cancelled = false;
    if (!key) {
      setNames(new Map());
      return;
    }
    (async () => {
      try {
        const map = await fetchAmNameMap(key.split(','));
        if (!cancelled) setNames(map);
      } catch (err) {
        // A missing display name degrades to the eid — never blocks the row.
        console.error('[incentive] name resolution failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  return useCallback((eid: string | null) => (eid ? names.get(eid) ?? eid : null), [names]);
}

// ----- Payouts that need action, across ALL periods ------------------------
//
// CW-N 3a. The Payouts tab used to be blank until a period was chosen from
// the queue, which meant a July row left behind in August was invisible
// unless someone went looking for it. No period filter here on purpose:
// anything outstanding — this period or any earlier one — surfaces itself.

const NEEDS_ACTION_STATUSES: IncentivePayoutStatus[] = [
  'pending_review',
  'am_validated',
];

const RESOLVED_STATUSES: IncentivePayoutStatus[] = [
  'verified',
  'sent_to_finance',
  'paid',
  'disqualified',
];

function usePayoutsByStatus(
  statuses: IncentivePayoutStatus[],
  label: string,
  refreshKey: number,
) {
  const [rows, setRows] = useState<PayoutDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const key = statuses.join(',');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data, error: e } = await supabase
          .from('v_incentive_payouts_detail')
          .select('*')
          .in('status', key.split(','))
          .order('period', { ascending: false })
          .order('facility_name', { ascending: true });
        if (cancelled) return;
        if (e) throw e;
        setRows((data ?? []) as PayoutDetail[]);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, `Failed to load ${label}.`, 'incentive'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key, label, tick, refreshKey]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { rows, isLoading, error, refetch };
}

/** pending_review + am_validated, every period, most recent first. */
export function usePayoutsNeedingAction(refreshKey = 0) {
  return usePayoutsByStatus(NEEDS_ACTION_STATUSES, 'outstanding payouts', refreshKey);
}

/** verified / sent_to_finance / paid / disqualified — the collapsed groups. */
export function usePayoutsResolved(refreshKey = 0) {
  return usePayoutsByStatus(RESOLVED_STATUSES, 'completed payouts', refreshKey);
}

// ----- Payouts (detail) for one period + type ------------------------------
//
// Still used for the drill-in when a specific period is opened from the
// queue. Do not delete: the queue's "Open payouts" depends on it.

export function usePayouts(
  period: string | null,
  payoutType: IncentivePayoutType,
  refreshKey = 0,
) {
  const [rows, setRows] = useState<PayoutDetail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!period) {
      setRows([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data, error: e } = await supabase
          .from('v_incentive_payouts_detail')
          .select('*')
          .eq('period', period)
          .eq('payout_type', payoutType)
          .order('facility_name', { ascending: true });
        if (cancelled) return;
        if (e) throw e;
        setRows((data ?? []) as PayoutDetail[]);
      } catch (err) {
        if (cancelled) return;
        console.error('[incentive] payouts load failed', err);
        setError(errorMessage(err, 'Failed to load payouts.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [period, payoutType, tick, refreshKey]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { rows, isLoading, error, refetch };
}

// ----- Analytics (3 roll-ups) ----------------------------------------------

export function useIncentiveAnalytics() {
  const [cost, setCost] = useState<CostByPeriod[]>([]);
  const [rateBySub, setRateBySub] = useState<PayoutRateBySub[]>([]);
  const [complimentRate, setComplimentRate] = useState<ComplimentRate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [c, r, q] = await Promise.all([
          supabase.from('v_incentive_cost_by_period').select('*'),
          supabase.from('v_incentive_payout_rate_by_sub').select('*'),
          supabase.from('v_incentive_compliment_rate').select('*'),
        ]);
        if (cancelled) return;
        if (c.error) throw c.error;
        if (r.error) throw r.error;
        if (q.error) throw q.error;
        setCost((c.data ?? []) as CostByPeriod[]);
        setRateBySub((r.data ?? []) as PayoutRateBySub[]);
        setComplimentRate((q.data ?? []) as ComplimentRate[]);
      } catch (err) {
        if (cancelled) return;
        console.error('[incentive] analytics load failed', err);
        setError(
          errorMessage(err, 'Failed to load analytics.'),
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { cost, rateBySub, complimentRate, isLoading, error, refetch };
}

// ----- Finance handoff (approved, awaiting payment) ------------------------

export function useFinanceHandoff() {
  const [rows, setRows] = useState<FinanceHandoffRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data, error: e } = await supabase
          .from('v_incentive_finance_handoff')
          .select('*')
          .order('period', { ascending: false });
        if (cancelled) return;
        if (e) throw e;
        setRows((data ?? []) as FinanceHandoffRow[]);
      } catch (err) {
        if (cancelled) return;
        console.error('[incentive] finance handoff load failed', err);
        setError(errorMessage(err, 'Failed to load handoff.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { rows, isLoading, error, refetch };
}

// ----- Actions --------------------------------------------------------------

// Materialize incentive_payouts rows for a period (idempotent; full-AM only,
// enforced by the RPC + INSERT RLS). Returns created/skipped counts.
export async function generateIncentivePayouts(
  period: string,
  payoutType: IncentivePayoutType,
): Promise<{ created: number; skipped: number }> {
  const { data, error } = await supabase.rpc('fn_generate_incentive_payouts', {
    p_period: period,
    p_payout_type: payoutType,
  });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as
    | { created_count: number; skipped_count: number }
    | null;
  return { created: row?.created_count ?? 0, skipped: row?.skipped_count ?? 0 };
}

// Validate / advance / override a payout. The BEFORE UPDATE trigger stamps
// verified_at / routed_to_finance_at / paid_at + actor eids, and REQUIRES a
// justification when overriding the engine (paying a disqualified row, or
// disqualifying a qualified one).
export async function setPayoutStatus(
  payoutId: string,
  status: IncentivePayoutStatus,
  justification?: string,
): Promise<void> {
  const patch: Record<string, unknown> = { status };
  if (justification && justification.trim()) {
    patch.override_justification = justification.trim();
  }
  const { error } = await supabase
    .from('incentive_payouts')
    .update(patch)
    .eq('payout_id', payoutId);
  if (error) throw error;
}
